import type {Params, RerouterLocation} from 'src/rerouter';
import type {Dispatch, GetState} from 'src/reducers';

import * as React from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import {useLocation, useParams} from 'src/rerouter';
import {useStore} from 'react-redux';

import IndexStore from 'src/stores';
import nameHoc from './nameHoc';
import useFluxStore from './useFluxStore';
import {handleGenericApiError} from 'src/utils/errors';

/**
 * DO NOT USE THIS.
 * This is a legacy HOC for backwards compatibility.
 */
export function provideHooks<C>({
  required,
  deferred,
  clientSide,
}: {
  required?: (arg1: RedialArgs) => Promise<unknown>;
  deferred?: (arg1: RedialArgs) => Promise<unknown>;
  clientSide?: (arg1: RedialArgs) => Promise<unknown>;
}): (WrappedComponent: React.ComponentType<C>) => React.ComponentType<C> {
  return (WrappedComponent: React.ComponentType<C>) =>
    nameHoc(
      // @ts-expect-error - TS7006 - Parameter 'props' implicitly has an 'any' type.
      hoistNonReactStatics((props) => {
        const location = useLocation();
        const params = useParams();
        const fluxStore = useFluxStore();
        const reduxStore = useStore();

        const [loaded, setLoaded] = React.useState(!required);

        React.useEffect(() => {
          let canceled = false;

          const redialArgs = {
            params,
            location,
            store: fluxStore,
            dispatch: reduxStore.dispatch,
            getState: reduxStore.getState,
          } as const;

          const getDeferred = () => {
            if (!canceled) {
              deferred &&
                Promise.resolve(deferred(redialArgs)).catch(
                  handleGenericApiError,
                );
              clientSide &&
                Promise.resolve(clientSide(redialArgs)).catch(
                  handleGenericApiError,
                );
              setLoaded(true);
            }
          };

          if (required) {
            // TODO (kyle): handle errors
            Promise.resolve(required(redialArgs)).then(
              getDeferred,
              handleGenericApiError,
            );
          } else {
            getDeferred();
          }

          return () => {
            canceled = true;
          };
          // NOTE (kyle): this is a heuristic. we could hypothetically use `props.pathname`.
        }, [location.href]);

        return loaded ? <WrappedComponent {...props} /> : null;
      }, WrappedComponent),
      WrappedComponent,
      'provideHooks',
    );
}

type RedialArgs = {
  params: Params;
  location: RerouterLocation;
  store: IndexStore;
  dispatch: Dispatch;
  getState: GetState;
};
